import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEO from '@/components/base/SEO';
import { testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        body.append(key, value as string);
      }
    });
    fetch('https://readdy.ai/api/form/d9rct3knlsngrm4lmacg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    })
      .then(res => res.text())
      .then(text => {
        let parsed: Record<string, unknown> = {};
        try { parsed = JSON.parse(text); } catch (_e) { parsed = {}; }
        const code = parsed?.code;
        if (code === 'OK') {
          setFormStatus('success');
          form.reset();
          setTimeout(() => setFormStatus('idle'), 3000);
        } else {
          const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。';
          setFormError(msg);
          setFormStatus('error');
        }
      })
      .catch(() => {
        setFormError('网络错误，请稍后再试。');
        setFormStatus('error');
      });
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '精品一区二区',
    url: 'https://jingpinyiquercu.com/',
    description: '精品一区二区是一个高品质内容聚合平台，汇聚全网优质内容资源，严格分区筛选，按区域分类整理。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@jingpinyiquercu.com',
      availableLanguage: ['Chinese'],
    },
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 精品一区二区',
    description: '了解精品一区二区的品牌故事、发展历程与核心价值',
    url: 'https://jingpinyiquercu.com/about',
  };

  return (
    <>
      <SEO
        title="关于我们 - 精品一区二区·优选内容聚合平台"
        description="精品一区二区汇聚全网优质内容资源，严格分区筛选。了解我们的品牌故事、平台理念与核心价值，联系我们获取更多信息。"
        keywords="关于精品一区二区,精品内容平台介绍,内容聚合平台,联系我们,品牌故事"
        canonicalUrl="https://jingpinyiquercu.com/about"
        schemaData={{ ...orgSchema, ...aboutSchema }}
      />

      <Navbar />

      <main>
        <section className="relative pt-24 pb-14 md:pt-28 md:pb-16 bg-primary-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
            <div className="text-center mt-8">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 mb-4">
                关于精品一区二区
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                致力于打造高品质内容聚合平台，让优质内容触手可及
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
                  品牌故事
                </span>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6 leading-snug">
                  聚合精品内容，传递<span className="text-primary-600">知识价值</span>
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    精品一区二区创立于2024年，是一个专注于高品质内容聚合与分发的在线平台。我们深知在信息爆炸的时代，找到真正有深度、有价值的优质内容变得越来越困难。
                  </p>
                  <p>
                    因此，我们建立了严格的内容筛选机制和分区管理体系。"精品一区"聚焦科技前沿、商业洞察、人文思考等深度内容；"精品二区"关注健康养生、旅行探索、美食文化、艺术设计等生活美学内容。
                  </p>
                  <p>
                    我们的编辑团队由来自不同领域的资深内容创作者组成，每位成员都在各自领域拥有丰富的经验和独到的见解。我们坚持原创优先、质量至上的原则，确保每一篇文章都能为读者带来真正的收获。
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20office%20workspace%20with%20wooden%20desk%20books%20and%20plants%2C%20soft%20natural%20sunlight%20streaming%20through%20window%2C%20creative%20atmosphere%2C%20warm%20amber%20and%20cream%20tones%2C%20editorial%20interior%20photography%20style%20with%20cozy%20vibe&width=700&height=500&seq=about-brand-01&orientation=landscape"
                  alt="精品一区二区办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 whitespace-nowrap">
                核心价值
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                我们的价值观
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-shield-check-line', title: '品质优先', desc: '严格的内容审核标准，确保每篇文章都具有真实价值和阅读意义' },
                { icon: 'ri-stack-line', title: '分区管理', desc: '科学的内容分区体系，让用户快速定位感兴趣的内容领域' },
                { icon: 'ri-lightbulb-line', title: '知识赋能', desc: '不只传递信息，更致力于帮助读者获得真正的知识增长' },
                { icon: 'ri-user-heart-line', title: '用户至上', desc: '以用户需求为导向，持续优化内容质量和阅读体验' },
              ].map((v, i) => (
                <div key={i} className="bg-background-50 rounded-xl border border-background-200/70 p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-800 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full mb-4 whitespace-nowrap">
                发展历程
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                我们的足迹
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              {[
                { year: '2024 Q1', title: '平台创立', desc: '精品一区二区正式上线，确立分区管理的内容模式' },
                { year: '2024 Q3', title: '内容体系完善', desc: '建立专业编辑团队，完成一区二区内容方向的精细化定位' },
                { year: '2024 Q4', title: '用户快速增长', desc: '月活跃用户突破10万，优质内容获得读者广泛认可' },
                { year: '2025 Q2', title: '内容生态扩展', desc: '签约作者超过100位，日更新文章量稳步提升至20篇以上' },
                { year: '2025 Q4', title: '平台升级', desc: '全面优化用户体验，推出个性化推荐和内容订阅功能' },
                { year: '2026', title: '持续成长', desc: '持续深耕内容质量，致力于成为中文领域最受信赖的精品内容平台' },
              ].map((item, i) => (
                <div key={i} className="flex gap-6 pb-8 last:pb-0">
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-1.5 ${i === 0 ? 'bg-primary-500' : 'bg-primary-300'}`}></div>
                    {i < 5 && <div className="w-0.5 flex-1 bg-background-200 mt-1"></div>}
                  </div>
                  <div>
                    <span className="text-xs font-medium text-primary-600 whitespace-nowrap">{item.year}</span>
                    <h3 className="text-base font-bold text-foreground-800 mt-1">{item.title}</h3>
                    <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 whitespace-nowrap">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
                用户对我们的评价
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {testimonials.map(t => (
                <div key={t.id} className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-primary-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800 whitespace-nowrap">{t.name}</p>
                      <p className="text-xs text-foreground-400 whitespace-nowrap">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6 leading-snug">
                  期待与您<span className="text-primary-600">交流</span>
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-8">
                  无论您有内容合作意向、投稿需求，还是对平台有任何建议和意见，都欢迎通过以下方式与我们取得联系。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-800 mb-1">电子邮件</h4>
                      <p className="text-sm text-foreground-500">contact@jingpinyiquercu.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-800 mb-1">办公地址</h4>
                      <p className="text-sm text-foreground-500">北京市朝阳区建国路88号SOHO现代城A座</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-foreground-800 mb-1">工作时间</h4>
                      <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-6">
                  发送消息
                </h3>
                <form
                  data-readdy-form="contact"
                  onSubmit={handleContactSubmit}
                  className="space-y-5"
                >
                  <div className="contact-alt-wrap">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-2">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="您的姓名"
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors duration-200 placeholder:text-foreground-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-2">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors duration-200 placeholder:text-foreground-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      主题
                    </label>
                    <input
                      type="text"
                      name="subject"
                      placeholder="消息主题"
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors duration-200 placeholder:text-foreground-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-2">
                      消息内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder="请输入您的消息内容..."
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none placeholder:text-foreground-400"
                    ></textarea>
                  </div>

                  {formStatus === 'success' && (
                    <div className="p-4 rounded-lg bg-primary-50 text-primary-700 text-sm">
                      <i className="ri-check-line mr-2"></i>
                      消息发送成功！我们会尽快与您联系。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-4 rounded-lg bg-accent-100 text-accent-700 text-sm">
                      <i className="ri-error-warning-line mr-2"></i>
                      {formError || '发送失败，请稍后再试。'}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送消息
                    <i className="ri-send-plane-line ml-2"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}